import { cx, Icon, type IconId } from '@amumin/ui-core';

import styles from './SegmentedControl.module.css';

export interface SegmentOption<T extends string = string> {
  value: T;
  label: string;
  icon?: IconId;
}

export interface SegmentedControlProps<T extends string = string> {
  /** Toegankelijke naam van de groep, bv. "Weergave". */
  label: string;
  options: ReadonlyArray<SegmentOption<T>>;
  value: T;
  onChange: (value: T) => void;
  size?: 'small' | 'normal';
  /** Volle breedte: segmenten delen de ruimte (mobiel). */
  block?: boolean;
  name?: string;
}

/**
 * Keuze uit 2–5 gelijkwaardige opties (Dag/Week/Maand, Ieder apart/Samen/Wie eerst).
 * Radiogroep-semantiek; de actieve optie ligt als een rustig wit blok op een verzonken baan.
 */
export function SegmentedControl<T extends string>({
  label,
  options,
  value,
  onChange,
  size = 'normal',
  block,
  name,
}: SegmentedControlProps<T>) {
  return (
    <div
      class={cx(styles.group, size === 'small' && styles.small, block && styles.block)}
      role="radiogroup"
      aria-label={label}
      data-name={name}
    >
      {options.map((o) => {
        const checked = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={checked ? 'true' : 'false'}
            class={cx(styles.segment, checked && styles.checked)}
            onClick={() => onChange(o.value)}
          >
            {o.icon ? <Icon id={o.icon} size={size === 'small' ? 16 : 18} /> : null}
            <span>{o.label}</span>
          </button>
        );
      })}
    </div>
  );
}
